import { AnchorButton, H2, Icon, Label, LinkButton, Row, useFormatDateTime, useHasPermission } from "@dgs/core";
import { isBefore } from "date-fns";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { EventRating } from "../../../../shared/events/EventRating";
import { shouldSkipOffboarding } from "../../../../shared/events/offboarding/eventOffboardingStepperUtils";
import { LinkToOffboardingButton } from "../../../../shared/events/offboarding/LinkToOffboardingButton";
import { EventDetailResource } from "../../shared/types";
import { EventParticipationAction } from "./EventParticipationAction";
import { EventUserParticipationStatusLabel } from "./EventUserParticipationStatusLabel";

interface Props {
	event: EventDetailResource;
}

const HeadlineContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	gap: ${({ theme }) => theme.spacing(4)};

	${({ theme }) => theme.breakpoints.tablet} {
		grid-column: 1 / span 2;
	}
`;

const Headline = styled(H2)`
	font-family: ${({ theme }) => theme.fonts.body};
	font-weight: ${({ theme }) => theme.fonts.weights.bold};
	font-size: 26px;
`;

const ValueWithIcon = styled.div`
	display: grid;
	gap: ${({ theme }) => theme.spacing(2)};
	grid-template-columns: 20px max-content;
	grid-auto-rows: max-content;
	align-items: center;
	justify-items: center;
	justify-content: center;
	line-height: 1;
`;

const StyledIcon = styled(Icon)`
	width: 20px;
	height: 20px;
`;

export const EventDetailsHeadline: FC<Props> = ({ event }) => {
	const { t } = useTranslation();
	const formatDateTime = useFormatDateTime();
	const hasEventEnded = isBefore(event.end, new Date());
	const canReadCostCategories = useHasPermission(["costCategory.read"]) || event.hasProjectMemberPermissions;

	return (
		<HeadlineContainer>
			<div>
				<Row>
					<Headline>{event.name}</Headline>
					<EventRating rating={event.rating} />
					<EventUserParticipationStatusLabel status={event.userParticipationStatus} />
				</Row>
				<Row $columnGap={8}>
					{event.isHighlight && (
						<Label color="primary">
							<ValueWithIcon>
								<StyledIcon $icon="star_filled" />
								<span>{t("Highlight")}</span>
							</ValueWithIcon>
						</Label>
					)}
					<ValueWithIcon>
						<StyledIcon $icon="calendar" />
						<span>
							{formatDateTime(event.start, {
								day: "2-digit",
								month: "2-digit",
								year: "numeric",
							})}
							{" - "}
							{formatDateTime(event.end, {
								day: "2-digit",
								month: "2-digit",
								year: "numeric",
							})}
						</span>
					</ValueWithIcon>
					{event.address && (
						<ValueWithIcon>
							<StyledIcon $icon="marker" />
							<span>
								{event.address.city}, {event.address.country.name}
							</span>
						</ValueWithIcon>
					)}
					<ValueWithIcon>
						<StyledIcon $icon="layers" />
						<span>{event.eventType.name}</span>
					</ValueWithIcon>
				</Row>
			</div>
			<Row $gap={2}>
				{hasEventEnded && event.hasUnansweredFeedback && (
					<LinkButton color="primary" to="../feedback">
						{t("Give feedback")}
					</LinkButton>
				)}
				{hasEventEnded &&
					!shouldSkipOffboarding(event.eventType) &&
					!event.hasFinishedOffboarding &&
					canReadCostCategories && <LinkToOffboardingButton eventType={event.eventType} />}
				<EventParticipationAction event={event} />
				{event.link && (
					<AnchorButton href={event.link} target="_blank">
						{t("To event")}
					</AnchorButton>
				)}
			</Row>
		</HeadlineContainer>
	);
};
